import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../lib/api'
import { transformTrips } from '../lib/transformers'
import { Plus, Trash2, Eye } from 'lucide-react'
import Pagination from '../components/Pagination'
import type { Trip } from '../types'

export default function TripsPage() {
  const navigate = useNavigate()
  const [trips, setTrips] = useState<Trip[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 20

  useEffect(() => {
    loadTrips()
  }, [currentPage])

  const loadTrips = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getTrips({
        page: currentPage,
        limit: itemsPerPage
      })
      let rawData = []
      let pagination = null

      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {trips: [], pagination: {}}}
        rawData = response.data?.data?.trips || response.data?.trips || response.data?.data || response.data?.items || []
        pagination = response.data?.data?.pagination || response.data?.pagination
      }

      // Transform snake_case to camelCase
      const data = transformTrips(Array.isArray(rawData) ? rawData : [])
      setTrips(data)

      // Update pagination info
      if (pagination) {
        setTotalPages(pagination.totalPages || Math.ceil(pagination.total / itemsPerPage) || 1)
        setTotalItems(pagination.total || rawData.length)
      } else {
        // If no pagination info, assume single page
        setTotalPages(1)
        setTotalItems(rawData.length)
      }
    } catch (error) {
      console.error('Failed to load trips:', error)
      setTrips([])
    } finally {
      setLoading(false)
    }
  }


  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this trip?')) return

    try {
      await adminApi.deleteTrip(id)
      loadTrips()
    } catch (error) {
      console.error('Failed to delete trip:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trips</h1>
          <p className="text-gray-600 mt-1">Manage trips and create official content</p>
        </div>
        <button
          onClick={() => navigate('/trips/new')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Trip
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : trips.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No trips found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-gray-700">Title</th>
                    <th className="pb-3 font-medium text-gray-700">Author</th>
                    <th className="pb-3 font-medium text-gray-700">Status</th>
                    <th className="pb-3 font-medium text-gray-700">Privacy</th>
                    <th className="pb-3 font-medium text-gray-700">Created</th>
                    <th className="pb-3 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((trip) => (
                    <tr key={trip.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          {trip.coverImageUrl && (
                            <img
                              src={trip.coverImageUrl}
                              alt={trip.title}
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <p
                              className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                              onClick={() => navigate(`/trips/${trip.id}`)}
                            >
                              {trip.title}
                            </p>
                            <p className="text-sm text-gray-600 truncate max-w-xs">
                              {trip.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-gray-700">{trip.userName}</td>
                      <td className="py-3">
                        <span className={`badge ${
                          trip.status === 'active' ? 'badge-success' :
                          trip.status === 'planning' ? 'badge-warning' :
                          trip.status === 'completed' ? 'badge-info' :
                          'badge-secondary'
                        }`}>
                          {trip.status}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`badge ${
                          trip.privacy === 'public' ? 'badge-success' :
                          trip.privacy === 'unlisted' ? 'badge-warning' :
                          'badge-info'
                        }`}>
                          {trip.privacy}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-700">
                        {new Date(trip.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/trips/${trip.id}`)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 text-blue-600 rounded-lg text-sm font-medium transition-colors"
                          >
                            <Eye className="h-4 w-4 inline mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => handleDelete(trip.id)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors"
                          >
                            <Trash2 className="h-4 w-4 inline mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>
    </div>
  )
}
